import React, { useCallback, useEffect, useState } from "react";

import { useIntl } from "react-intl";
import { Navigate, useNavigate } from "react-router-dom";

import ContentLayout from "Components/ContentLayout";
import FailedActivitySticker from "Components/EnvironmentTree/FailedActivitySticker";
import EnvironmentBackupModal from "Containers/NavBar/Environment/BackupModal";
import GoLiveButton from "Containers/NavBar/Environment/GoLiveButton";
import MoreEnvironmentAction, {
  MORE_ACTION_ID
} from "Containers/NavBar/Environment/moreActions/MoreEnvironmentAction";
import { SourceOperations } from "Containers/NavBar/Environment/sourceOperations";
import { CloseButton, Link } from "ds/Button";
import useDecodedParams from "Hooks/useDecodedParams";
import { useLocalForage } from "Hooks/useLocalForage";
import useMediaQuery from "Hooks/useMediaQuery";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import useUrls from "Hooks/useUrls";
import { isDg3 } from "Libs/utils";
import {
  getLogUrl,
  useShowFailedActivitySticker
} from "Reducers/activity/activity.hooks";
import { meSelector } from "Reducers/app/selectors";
import {
  environmentSelector,
  environmentLoadingSelector
} from "Reducers/environment";
import { isLoadingSelector } from "Reducers/environment/deployment";
import { selectHasDedicatedDeployment } from "Reducers/environment/environment.selectors";
import {
  selectFeatureOrganizationsEnabled,
  selectFeatureServiceTreeEnabled
} from "Reducers/featureFlags/featureFlags.selectors";
import { organizationProfileSelector } from "Reducers/organization/profile";
import { selectSubscriptionDetails as organizationSubscriptionSelector } from "Reducers/organization/subscription";
import { gitProjectSelector } from "Reducers/project/project";
import { loadProjectWizard } from "Reducers/project/wizard";
import { setEnvironmentsContext } from "Reducers/settingsMenu";
import { subscriptionSelector } from "Reducers/subscription";
import { withProjectRedirect } from "src/common/hocs/withProjectRedirect";
import ServicesDisplaySkeleton from "src/organization/common/components/ServicesDisplaySkeleton";
import ActivitySection from "src/organization/common/containers/ActivitySection/ActivitySection";
import InfoDropButtons from "src/organization/common/containers/InfoDropButtons";
import ServicesDisplay from "src/organization/common/containers/ServicesDisplay";
import PluginApp from "src/organization/pages/project/app/Page";
import ContentWizard from "src/organization/pages/project/containers/ContentWizard";
import BlackfireCard from "src/organization/pages/project/containers/ProjectOverview/BlackfireCard";
import { useShowResourcesSticker } from "src/organization/pages/project/containers/ProjectOverview/hooks/useShowResourcesSticker";
import { usePluginApps } from "src/root/pages/pluginApps/usePluginApps";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import OverviewSkeleton from "../overview/containers/Overview/OverviewSkeleton";
import EnvironmentOverview from "../overview/Page";

import * as S from "./Details.styles";

type DetailsProps = {
  children: React.ReactNode;
};

const Details = ({ children }: DetailsProps) => {
  const { environmentId, organizationId, projectId } = useDecodedParams<{
    environmentId: string;
    organizationId: string;
    projectId: string;
  }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const showResourcesSticker = useShowResourcesSticker();
  const { showFailedActivitySticker, lastFailedActivity } =
    useShowFailedActivitySticker();
  const failedActivityUrl = getLogUrl({
    organizationId,
    projectId,
    environmentId,
    activity: lastFailedActivity
  });

  const [pluginApps] = usePluginApps();

  const [areServicesReady, setAreServicesReady] = useState(false);
  const [openBackupModal, setBackupModalState] = useState(false);
  const [openSourceOperation, setOpenSourceOperation] = useState(false);
  const [isRuntimeOperation, setIsRuntimeOperation] = useState(false);

  const onMobile = useMediaQuery("(max-width: 900px)");
  const {
    value: hideBanner = true,
    setValue: setHideBanner,
    isLoading
  } = useLocalForage<boolean>("automated_code_updates_banner");

  const user = useAppSelector(meSelector);
  const environment = useSelectorWithUrlParams(environmentSelector);
  const envLoading = useSelectorWithUrlParams(environmentLoadingSelector);
  const project = useSelectorWithUrlParams(gitProjectSelector);

  const dedicatedDeployment = useSelectorWithUrlParams(
    selectHasDedicatedDeployment
  );
  const isDeploymentLoading = useSelectorWithUrlParams(isLoadingSelector);

  const accountTier = useAppSelector(state =>
    organizationProfileSelector(state, { organizationId })
  )?.account_tier;

  const enableServicesReady = useCallback(() => {
    setAreServicesReady(true);
  }, []);

  useEffect(() => {
    if (!project?.id || !environment?.id) return;
    dispatch(
      loadProjectWizard({
        organizationId,
        projectId
      })
    );
  }, [dispatch, project, environment, organizationId, projectId]);

  const toggleBackupModal = () => {
    setBackupModalState(!openBackupModal);
  };

  const subscription = useAppSelector(state => {
    return selectFeatureOrganizationsEnabled(state)
      ? organizationSubscriptionSelector(state, {
          organizationId,
          subscriptionId: project?.getSubscriptionId()
        })
      : subscriptionSelector(state, {
          organizationId,
          projectId,
          id: project?.getSubscriptionId()
        });
  });

  const serviceTreeEnabled = useAppSelector(selectFeatureServiceTreeEnabled);

  const isDg3Project = isDg3(subscription?.enterprise_tag, dedicatedDeployment);

  const serviceTreeOrDg3 = serviceTreeEnabled || isDg3Project;

  const [
    mergeActionUrl,
    branchActionUrl,
    syncActionUrl,
    redeployActionUrl,
    resumeActionUrl
  ] = useUrls([
    "organization.project.environment.actions.merge",
    "organization.project.environment.actions.branch",
    "organization.project.environment.actions.sync",
    "organization.project.environment.actions.redeploy",
    "organization.project.environment.actions.resume"
  ]);

  const handleMergeClicked = e => {
    if (environment?.hasLink("#merge")) {
      e.preventDefault();
      (document.activeElement as HTMLElement | null)?.blur();
      navigate(mergeActionUrl);
    }
  };

  const handleBranchClicked = e => {
    if (environment?.hasLink("#branch")) {
      e.stopPropagation();
      (document.activeElement as HTMLElement | null)?.blur();
      navigate(branchActionUrl);
    }
  };

  const handleSyncClicked = e => {
    if (environment?.hasLink("#synchronize")) {
      e.stopPropagation();
      (document.activeElement as HTMLElement | null)?.blur();
      navigate(syncActionUrl);
    }
  };

  const onClickMoreAction = props => {
    switch (props.id) {
      case MORE_ACTION_ID.BACKUP:
        if (environment?.hasLink("#backup")) {
          props.event.preventDefault();
          (document.activeElement as HTMLElement | null)?.blur();
          toggleBackupModal();
        }
        break;
      case MORE_ACTION_ID.BRANCH:
        handleBranchClicked(props.event);
        break;
      case MORE_ACTION_ID.MERGE:
        handleMergeClicked(props.event);
        break;
      case MORE_ACTION_ID.REDEPLOY:
        environment?.hasLink("#redeploy") && navigate(redeployActionUrl);
        props.event.preventDefault();
        break;
      case MORE_ACTION_ID.RESUME:
        navigate(resumeActionUrl);
        props.event.preventDefault();
        break;
      case MORE_ACTION_ID.SOURCE_OPS:
        setOpenSourceOperation(!openSourceOperation);
        setIsRuntimeOperation(props?.isRuntime);
        props.event.preventDefault();
        break;
      case MORE_ACTION_ID.SYNC:
        props.event.preventDefault();
        handleSyncClicked(props.event);
        break;
      case MORE_ACTION_ID.DELETE_ENVIRONMENT:
      //TODO DELETE delete environment
    }
  };

  if (!environment) return null;

  // Redirect to settings page if env doesn't exist and name matches
  // settings pages [settings, integrations. plan, wait]
  if (!envLoading && Object.entries(environment).length === 0) {
    if (["settings", "integrations", "plan", "wait"].includes(environmentId)) {
      const url = `/${organizationId}/${projectId}/-/${environmentId}`;
      return <Navigate to={url} />;
    }
  }

  const settingsLink = `/${organizationId}/${projectId}/${environmentId}/settings`;

  const handleSettingsClicked = e => {
    e.stopPropagation();
    dispatch(setEnvironmentsContext(environmentId));
    navigate(settingsLink);
  };

  const showDisplaySkeleton = !areServicesReady || isDeploymentLoading;

  return (
    <ContentLayout>
      <S.EnvMenuFlex>
        {onMobile && (
          <S.MobileSettingsWrapper>
            <S.MobileSettingContainer>
              <GoLiveButton />
              {project && <InfoDropButtons noPadding project={project} />}
            </S.MobileSettingContainer>
            <S.MobileSettingContainer>
              <S.SettingsIcon onClick={handleSettingsClicked} />
              <MoreEnvironmentAction
                onClick={onClickMoreAction}
                environment={environment}
              />
            </S.MobileSettingContainer>
          </S.MobileSettingsWrapper>
        )}
      </S.EnvMenuFlex>

      <S.ColumnWrapper>
        <aside className="col-4">
          <S.ListGroup
            className="environment-info"
            aria-labelledby="environment-heading"
          >
            {envLoading ? (
              <OverviewSkeleton />
            ) : (
              <S.ListGroup aria-labelledby="environment-info-heading">
                <S.OverviewBlock>
                  <EnvironmentOverview />
                  {children}
                </S.OverviewBlock>
              </S.ListGroup>
            )}
          </S.ListGroup>

          {Object.entries(pluginApps)
            .filter(([, plugin]) =>
              plugin.hooks?.some(
                hook =>
                  hook.route === "/:organizationId/:projectId/:environmentId" &&
                  hook.type === "overview"
              )
            )
            .map(([appId]) => (
              <S.ListGroup key={appId}>
                <PluginApp
                  applicationId={appId}
                  route="/:organizationId/:projectId/:environmentId"
                  type="overview"
                  organizationId={organizationId}
                  projectId={projectId}
                  environmentId={environmentId}
                />
              </S.ListGroup>
            ))}

          <S.ListGroup aria-labelledby="bf-heading">
            <BlackfireCard />
          </S.ListGroup>

          {serviceTreeOrDg3 && (
            <S.ListGroup aria-labelledby="environment-services-heading">
              {showDisplaySkeleton && <ServicesDisplaySkeleton />}
              <S.ServicesBlock>
                <S.Title tag="h2">
                  {intl.formatMessage({ id: "apps_services" })}
                </S.Title>
                <ServicesDisplay
                  hasInfoTooltip
                  onLoadEnd={enableServicesReady}
                />
              </S.ServicesBlock>
            </S.ListGroup>
          )}
        </aside>
        <div>
          {(accountTier === "elite" || accountTier === "enterprise") &&
            !hideBanner &&
            !isLoading && (
              <S.BannerWrapper
                priority="activity"
                closeBanner={
                  <CloseButton onClick={() => setHideBanner(true)} />
                }
                text={
                  <S.BannerContent>
                    <S.BannerText>
                      <S.BannerTitle>
                        {intl.formatMessage({
                          id: "banner.update_code_title"
                        })}
                      </S.BannerTitle>
                      <S.BannerDescription>
                        {intl.formatMessage({
                          id: "banner.update_code_description"
                        })}
                      </S.BannerDescription>
                    </S.BannerText>
                    <Link
                      analyticId="learnhow"
                      variant="primary"
                      href={intl.formatMessage({
                        id: "links.documentation.automated_updates"
                      })}
                    >
                      {intl.formatMessage({
                        id: "learnhow",
                        defaultMessage: "Learn how"
                      })}
                    </Link>
                  </S.BannerContent>
                }
              />
            )}
          <ContentWizard
            subscription={subscription}
            project={project}
            environmentId={environmentId}
            user={user}
          />
          {showFailedActivitySticker && failedActivityUrl && (
            <FailedActivitySticker url={failedActivityUrl} />
          )}
          <ActivitySection
            context="environment"
            showResourcesSticker={Boolean(
              showResourcesSticker && environmentId
            )}
            excludeActivies={["filter.integration"]}
          />
        </div>
      </S.ColumnWrapper>

      <EnvironmentBackupModal
        isOpen={openBackupModal}
        closeModal={toggleBackupModal}
      />
      <SourceOperations
        organizationId={organizationId}
        projectId={projectId}
        environmentId={environmentId}
        isOpen={openSourceOperation}
        isRuntime={isRuntimeOperation}
        onClose={() => {
          setOpenSourceOperation(false);
        }}
      />
    </ContentLayout>
  );
};

export default withProjectRedirect(Details);
